import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Btn, IconBtn } from '@/components/editor-chrome/ui';

export type SuggestionKind = 'tone' | 'fact' | 'spelling' | 'grammar';

export interface SuggestionData {
  kind: SuggestionKind;
  title: string;
  detail: string;
  /** Optional one-click replacement preview shown in the "Suggest" row. */
  fix?: string;
  /** Rule id — when present, an "Ignore rule" button is rendered. */
  ruleId?: string;
}

export interface SuggestionPopoverProps {
  open: boolean;
  /** Element the popover is anchored to (the .ed-suggestion span). */
  anchorEl: HTMLElement | null;
  suggestion: SuggestionData | null;
  onApply: () => void;
  onDismiss: () => void;
  /** When provided AND suggestion.ruleId is set, render an "Ignore rule" action. */
  onIgnoreRule?: (ruleId: string) => void;
  className?: string;
}

const POPOVER_WIDTH = 320;

/**
 * Grammarly-style suggestion popover. Anchored to a `.ed-suggestion` element.
 * Repositions on scroll/resize and closes on outside-click + ESC.
 */
export function SuggestionPopover({
  open,
  anchorEl,
  suggestion,
  onApply,
  onDismiss,
  onIgnoreRule,
  className,
}: SuggestionPopoverProps) {
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  // Keep the popover glued to the anchor as the page scrolls or resizes.
  useEffect(() => {
    if (!open || !anchorEl) {
      setPos(null);
      return;
    }
    const update = () => {
      const r = anchorEl.getBoundingClientRect();
      const left = Math.max(12, Math.min(window.innerWidth - POPOVER_WIDTH - 12, r.left));
      const top = r.bottom + 6;
      setPos({ left, top });
    };
    update();
    window.addEventListener('resize', update);
    // Capture phase to catch scrolls in any ancestor scroll container.
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, anchorEl]);

  // Outside-click + ESC dismiss.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (anchorEl?.contains(target)) return;
      onDismiss();
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onDismiss();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open, anchorEl, onDismiss]);

  if (!open || !suggestion || !pos) return null;

  // Map suggestion.kind to the same accent color used for the underline.
  const accentClass =
    suggestion.kind === 'fact'
      ? 'bg-destructive'
      : suggestion.kind === 'tone'
      ? 'bg-[hsl(var(--semantic-depth))]'
      : suggestion.kind === 'spelling'
      ? 'bg-[hsl(var(--status-warning))]'
      : 'bg-[hsl(var(--status-warning))]';

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={suggestion.title}
      className={cn(
        'fixed z-50 w-[320px] rounded-[10px] border border-border bg-popover p-3',
        'text-popover-foreground shadow-lg',
        className,
      )}
      style={{ left: pos.left, top: pos.top }}
    >
      <div className="mb-1.5 flex items-center gap-1.5">
        <span className={cn('h-2 w-2 rounded-full', accentClass)} aria-hidden="true" />
        <div className="text-xs font-semibold">{suggestion.title}</div>
        <div className="flex-1" />
        <IconBtn
          icon={<X className="h-3 w-3" aria-hidden="true" />}
          label="Close suggestion"
          size={22}
          onClick={onDismiss}
        />
      </div>

      <div className="mb-2.5 text-[12.5px] leading-[1.5] text-muted-foreground">
        {suggestion.detail}
      </div>

      {suggestion.fix && (
        <div
          className={cn(
            'mb-2.5 rounded-[7px] border border-border bg-muted px-2.5 py-1.5',
            'text-[12.5px] text-foreground',
          )}
        >
          <span className="mr-1.5 text-[10.5px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
            Suggest
          </span>
          {suggestion.fix}
        </div>
      )}

      <div className="flex items-center justify-end gap-1.5">
        {onIgnoreRule && suggestion.ruleId && (
          <Btn variant="quiet" size="sm" onClick={() => onIgnoreRule(suggestion.ruleId!)}>
            Ignore rule
          </Btn>
        )}
        <Btn variant="ghost" size="sm" onClick={onDismiss}>
          Dismiss
        </Btn>
        {suggestion.fix && (
          <Btn variant="primary" size="sm" onClick={onApply}>
            Apply fix
          </Btn>
        )}
      </div>
    </div>
  );
}

export default SuggestionPopover;
